import React, { useMemo } from "react";
import { useExportedData } from "../../../../contexts/ExportedDataContext";
import { useNavigation } from "../../../../contexts/NavigationContext";
import Breadcrumbs from "../../../../components/Microgrid/BreadCrumbsFile";
import {
  formatWithCommas,
  formatSignedCurrency,
} from "../../utils/formatNumber";
import { buildSimulationCrumbs } from "../../utils/simulationCrumbs";
import StatusBadge from "./StatusBadge";
import TimeseriesRawTable from "./TimeseriesRawTable";
import TransferDailyRangeChart from "./TransferDailyRangeChart";
import "../Summary/SummaryPage.css";
import "./Verification.css";

// --- Constants ------------------------------------------------------------

// Energy-balance residual tolerances expressed as a fraction of peak energy.
// Pass: residual is below pass threshold -> green
// Warn: residual is below warn threshold -> yellow
// Fail: residual is above warn threshold -> red
const ENERGY_BALANCE_TOLERANCES = { pass: 0.005, warn: 0.01 };

// Cost anomaly thresholds.
const COST_RATE_WARN = 5; // $/kWh - flag implausible effective buy rates
const STORAGE_RT_BAND = [0.7, 1.0]; // discharge/charge ratio band (round-trip eff)

// CSV-column attribution. The strings here mirror the column names the
// production/consumption/transfer/storage workers read from each row.
const SOURCE_ATTRIBUTION_ENERGY = [
  { label: "Production", value: '\u03A3 "{Device} Solar (kWh)" + \u03A3 "{Device} Generator Output (kWh)"' },
  { label: "Consumption", value: '\u03A3 |"Load (kWh)"| (fallback: "Building Load Load (kWh)")' },
  { label: "Grid Import / Export", value: 'signed "Totals Total Transfer (kWh)" / "Energy Buy/Sell (kWh)" / "Utility Grid Import (kWh) - Utility Grid Export (kWh)"' },
  { label: "Storage Charge / Discharge", value: '\u03A3 "{Device} {Type} Charge (kWh)" + \u03A3 "{Device} {Type} Discharge (kWh)" (or signed Charge/Discharge column)' },
];

const SOURCE_ATTRIBUTION_COST = [
  { label: "Production cost", value: '\u03A3 "{Device} Production Cost ($)" (fallback: \u03A3 "Sell ($/kWh)" * production)' },
  { label: "Consumption cost", value: '\u03A3 "Building Load Load Cost ($)" (fallback: \u03A3 "Buy ($/kWh)" * load)' },
  { label: "Transfer cost", value: '\u03A3 "Totals Transfer Cost ($)" / "Cost or Return ($)" / "Costs Transfer Cost ($)"' },
  { label: "Storage cost", value: '\u03A3 "{Device} Storage Cost ($)" (fallback: signed buy/sell * charge)' },
];

const READY_STATE_LABELS = {
  production: "Production",
  consumption: "Consumption",
  transfer: "Transfer",
  storage: "Storage",
};

// --- Helpers -------------------------------------------------------------

// Safe numeric coercion for the string-formatted aggregates we receive from
// ExportedDataContext. Anything non-finite (undefined, "", "NaN", null) -> 0.
const toNum = (v) => {
  const n = Number.parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

const formatKwh = (value) => `${formatWithCommas(value)} kWh`;
const formatPct = (fraction) => `${(fraction * 100).toFixed(2)} %`;

const classifyEnergyBalance = (balancePct) => {
  if (balancePct < ENERGY_BALANCE_TOLERANCES.pass) return "pass";
  if (balancePct < ENERGY_BALANCE_TOLERANCES.warn) return "warn";
  return "fail";
};

const GLANCE_BADGE_LABEL = {
  pass: "Balanced",
  warn: "Close to tolerance",
  fail: "Outside tolerance",
  info: "Awaiting data",
};

// Mirrors readableSimName extraction in SummaryPage so the breadcrumb tail
// reads the same on both pages.
const extractReadableSimName = (fileName) => {
  if (!fileName) return "";
  const match = fileName.match(/\d{4}-\d{2}-\d{2}_[^.]+(?=\.csv)/);
  return match ? match[0].replace("timeseries-", "") : fileName;
};

// --- Period banner -------------------------------------------------------

const formatPeriodLabel = (sources) => {
  for (const src of sources) {
    if (!src) continue;
    if (src.view === "All Time View") return "All Time View";
    if (src.view && src.selectedPeriod) {
      return `${src.view}: ${src.selectedPeriod}`;
    }
    if (src.view) return src.view;
  }
  return "";
};

// --- Main component ------------------------------------------------------

export default function VerificationPage() {
  const {
    exportedTransferData,
    exportedProductionData,
    exportedProductionSummaryData,
    exportedData,
    exportedStorageSummaryData,
    chartDataReadyStates,
  } = useExportedData();
  const { timeSeriesFileName } = useNavigation();

  // Breadcrumb data sourced from session storage (mirrors SummaryPage).
  const crumbs = useMemo(() => {
    const customerEmail = sessionStorage.getItem("selectedCustomerEmail") || "";
    const customerPrefix = customerEmail.replace(/[@.]/g, "-");
    return buildSimulationCrumbs({
      jobTitle: sessionStorage.getItem("jobTitle"),
      customerFirstName: sessionStorage.getItem("customerFirstName"),
      customerPrefix,
      microgridDesign: sessionStorage.getItem("microgridDesign"),
      fileName:
        timeSeriesFileName || sessionStorage.getItem("timeSeriesFileName"),
    });
  }, [timeSeriesFileName]);

  // ------------------------------------------------------------------ derived

  const derived = useMemo(() => {
    const gridImport = Math.abs(toNum(exportedTransferData?.totalImport));
    const gridExport = Math.abs(toNum(exportedTransferData?.totalExport));
    const netImport = gridImport - gridExport;

    const production = toNum(exportedProductionSummaryData?.totalProduction);
    const consumption = Math.abs(toNum(exportedData?.totalAreaUnderCurve));

    const storageCharge = toNum(exportedStorageSummaryData?.chargeTotal);
    const storageDischarge = toNum(exportedStorageSummaryData?.dischargeTotal);
    const storageNet = storageDischarge - storageCharge; // +ve = battery releasing

    const inflows = production + gridImport + storageDischarge;
    const outflows = consumption + gridExport + storageCharge;
    const residual = inflows - outflows;

    const peakEnergy = Math.max(
      production,
      consumption,
      gridImport,
      gridExport,
      storageCharge,
      storageDischarge,
      1,
    );
    const balancePct = Math.abs(residual) / peakEnergy;

    const productionCost = toNum(exportedProductionSummaryData?.totalCost);
    const consumptionCost = toNum(exportedData?.totalCost);
    const transferCost = toNum(exportedTransferData?.value);
    const storageCost = toNum(exportedStorageSummaryData?.value);
    const totalCost =
      productionCost + consumptionCost + transferCost + storageCost;

    return {
      gridImport,
      gridExport,
      netImport,
      production,
      consumption,
      storageCharge,
      storageDischarge,
      storageNet,
      residual,
      peakEnergy,
      balancePct,
      productionCost,
      consumptionCost,
      transferCost,
      storageCost,
      totalCost,
    };
  }, [
    exportedTransferData,
    exportedProductionSummaryData,
    exportedData,
    exportedStorageSummaryData,
  ]);

  // -------------------------------------------------------------- readiness

  const missingReady = useMemo(() => {
    const ready = chartDataReadyStates || {};
    const required = ["production", "consumption", "transfer", "storage"];
    return required.filter((key) => !ready[key]);
  }, [chartDataReadyStates]);

  const isDataReady = missingReady.length === 0;

  // -------------------------------------------------------------- anomalies

  const costAnomalies = useMemo(() => {
    if (!isDataReady) return [];
    const issues = [];
    if (derived.production > 0 && derived.productionCost === 0) {
      issues.push(
        "Production has energy but no cost - check device pricing columns.",
      );
    }
    if (derived.consumption > 0 && derived.consumptionCost === 0) {
      issues.push(
        "Consumption has energy but no cost - check Buy ($/kWh) or Building Load Load Cost ($) columns.",
      );
    }
    if (Math.abs(derived.netImport) > 0 && derived.transferCost === 0) {
      issues.push(
        "Transfer has net flow but no cost - check Transfer Cost ($) columns.",
      );
    }
    if (derived.netImport > 0) {
      const rate = Math.abs(derived.transferCost) / derived.netImport;
      if (rate > COST_RATE_WARN) {
        issues.push(
          `Implausible effective buy rate $${rate.toFixed(2)} / kWh (over $${COST_RATE_WARN.toFixed(2)} threshold).`,
        );
      }
    }
    return issues;
  }, [derived, isDataReady]);

  const storageRoundTrip = useMemo(() => {
    if (!isDataReady) return null;
    const { storageCharge, storageDischarge } = derived;
    if (storageCharge <= 0 || storageDischarge <= 0) return null;
    const ratio = storageDischarge / storageCharge;
    const [lo, hi] = STORAGE_RT_BAND;
    if (ratio < lo || ratio > hi) {
      return {
        ratio,
        message: `Round-trip efficiency ${ratio.toFixed(2)}, outside [${lo.toFixed(2)}, ${hi.toFixed(2)}].`,
      };
    }
    return null;
  }, [derived, isDataReady]);

  // -------------------------------------------------------------- statuses

  const energyStatus = isDataReady
    ? classifyEnergyBalance(derived.balancePct)
    : "info";

  const energyBadgeText = isDataReady
    ? energyStatus === "pass"
      ? "Balanced"
      : `Residual ${formatKwh(derived.residual)} (${formatPct(derived.balancePct)} of peak)`
    : `Awaiting: ${missingReady.map((k) => READY_STATE_LABELS[k]).join(", ")}`;

  const costBadgeVariant = !isDataReady
    ? "info"
    : costAnomalies.length === 0
      ? "pass"
      : "warn";

  const costBadgeText = !isDataReady
    ? `Awaiting: ${missingReady.map((k) => READY_STATE_LABELS[k]).join(", ")}`
    : costAnomalies.length === 0
      ? "Looks reasonable"
      : `${costAnomalies.length} anomal${costAnomalies.length === 1 ? "y" : "ies"}`;

  // -------------------------------------------------------------- glance

  const glanceVariant = isDataReady ? energyStatus : "info";
  const glanceBadgeText = GLANCE_BADGE_LABEL[glanceVariant];

  // -------------------------------------------------------------- period

  const periodLabel = formatPeriodLabel([
    exportedTransferData,
    exportedProductionSummaryData,
    exportedData,
    exportedStorageSummaryData,
  ]);

  const readableSimName = extractReadableSimName(
    timeSeriesFileName || sessionStorage.getItem("timeSeriesFileName") || "",
  );

  // ---------------------------------------------------------------- render

  return (
    <div className="container microgrid-page verification-container">
      <h2 className="page-heading">Verification</h2>
      <Breadcrumbs crumbs={crumbs} />

      {/* At a glance ------------------------------------------------- */}
      <section
        className={`verification-glance verification-glance--${glanceVariant}`}
        aria-label="At a glance energy balance"
      >
        <div className="verification-glance-header">
          <h3 className="verification-glance-title">At a glance</h3>
          <StatusBadge variant={glanceVariant}>{glanceBadgeText}</StatusBadge>
        </div>
        <div className="verification-glance-grid">
          <div className="verification-glance-block">
            <span className="verification-glance-label">Total Energy Balance</span>
            <span
              className="verification-glance-number"
              data-testid="glance-residual"
            >
              {formatKwh(derived.residual)}
            </span>
          </div>
          <div className="verification-glance-block">
            <span className="verification-glance-label">
              Percent of peak energy
            </span>
            <span
              className="verification-glance-percent"
              title={`Residual / max(Production, Consumption, Grid Import, Grid Export, Storage Charge, Storage Discharge). Peak = ${formatKwh(derived.peakEnergy)}.`}
              data-testid="glance-pct"
            >
              {formatPct(derived.balancePct)}
            </span>
          </div>
        </div>
        <p className="verification-glance-caption">
          Residual = Production + Net Transfer + Storage Net &minus; Consumption.
          Period: {periodLabel || "n/a"}
          {readableSimName ? ` \u00B7 ${readableSimName}` : ""}.
        </p>
        {!isDataReady ? (
          <p
            className="verification-glance-caption"
            data-testid="glance-awaiting"
          >
            Awaiting data from:{" "}
            {missingReady.map((k) => READY_STATE_LABELS[k]).join(", ")}. Visit
            those page(s) at least once to populate the verification math.
          </p>
        ) : null}
      </section>

      {/* Two-column body --------------------------------------------- */}
      <div className="verification-columns">
        {/* ---------- Energy balance ---------- */}
        <section
          className="verification-section"
          aria-label="Energy balance details"
        >
          <div className="verification-section-header">
            <h2 className="verification-section-title">Energy balance (kWh)</h2>
            <StatusBadge variant={energyStatus}>{energyBadgeText}</StatusBadge>
          </div>
          <p className="verification-section-note">
            Sign convention: all values shown positive. Net rows are signed
            (positive = net import / net discharge); residual should be ~0.
          </p>
          <div className="verification-table" role="table">
            <div className="verification-row" role="row">
              <span className="verification-row-label" role="cell">
                Production
                {Array.isArray(exportedProductionData) &&
                exportedProductionData.length > 0 ? (
                  <details className="verification-row-drilldown">
                    <summary>
                      {exportedProductionData.length} device
                      {exportedProductionData.length === 1 ? "" : "s"}
                    </summary>
                    <ul>
                      {exportedProductionData.map((d) => (
                        <li key={d.deviceName}>
                          <span>
                            {d.deviceName} <em>({d.type})</em>
                          </span>
                          <span>
                            {formatKwh(toNum(d.totalProduction))} /{" "}
                            {formatSignedCurrency(toNum(d.totalCost))}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </details>
                ) : null}
              </span>
              <span className="verification-row-value" role="cell">
                {formatKwh(derived.production)}
              </span>
            </div>

            <div className="verification-row" role="row">
              <span className="verification-row-label" role="cell">
                Consumption
              </span>
              <span className="verification-row-value" role="cell">
                {formatKwh(derived.consumption)}
              </span>
            </div>

            <div className="verification-row" role="row">
              <span className="verification-row-label" role="cell">
                Grid Import
              </span>
              <span className="verification-row-value" role="cell">
                {formatKwh(derived.gridImport)}
              </span>
            </div>

            <div className="verification-row" role="row">
              <span className="verification-row-label" role="cell">
                Grid Export
              </span>
              <span className="verification-row-value" role="cell">
                {formatKwh(derived.gridExport)}
              </span>
            </div>

            <div className="verification-row verification-row--sub" role="row">
              <span className="verification-row-label" role="cell">
                Net Transfer (Import &minus; Export)
                <span className="verification-row-subtext">
                  positive = net import from grid
                </span>
              </span>
              <span className="verification-row-value" role="cell">
                {formatKwh(derived.netImport)}
              </span>
            </div>

            <div className="verification-row" role="row">
              <span className="verification-row-label" role="cell">
                Storage Charge
              </span>
              <span className="verification-row-value" role="cell">
                {formatKwh(derived.storageCharge)}
              </span>
            </div>

            <div className="verification-row" role="row">
              <span className="verification-row-label" role="cell">
                Storage Discharge
              </span>
              <span className="verification-row-value" role="cell">
                {formatKwh(derived.storageDischarge)}
              </span>
            </div>

            <div className="verification-row verification-row--sub" role="row">
              <span className="verification-row-label" role="cell">
                Storage Net (Discharge &minus; Charge)
                <span className="verification-row-subtext">
                  positive = battery releasing energy
                </span>
              </span>
              <span className="verification-row-value" role="cell">
                {formatKwh(derived.storageNet)}
              </span>
            </div>

            <div className="verification-row verification-row--total" role="row">
              <span className="verification-row-label" role="cell">Residual</span>
              <span
                className="verification-row-value"
                role="cell"
                data-testid="energy-residual"
              >
                {formatKwh(derived.residual)} ({formatPct(derived.balancePct)})
              </span>
            </div>
          </div>

          <details className="verification-source-attribution">
            <summary>Source attribution</summary>
            <dl>
              {SOURCE_ATTRIBUTION_ENERGY.map((row) => (
                <React.Fragment key={row.label}>
                  <dt>{row.label}</dt>
                  <dd>{row.value}</dd>
                </React.Fragment>
              ))}
            </dl>
          </details>
        </section>

        {/* ---------- Cost summary ---------- */}
        <section
          className="verification-section"
          aria-label="Cost summary details"
        >
          <div className="verification-section-header">
            <h2 className="verification-section-title">Cost summary ($)</h2>
            <StatusBadge variant={costBadgeVariant}>{costBadgeText}</StatusBadge>
          </div>
          <div className="verification-table" role="table">
            <div className="verification-row" role="row">
              <span className="verification-row-label" role="cell">Production</span>
              <span className="verification-row-value" role="cell">
                {formatSignedCurrency(derived.productionCost)}
              </span>
            </div>
            <div className="verification-row" role="row">
              <span className="verification-row-label" role="cell">Consumption</span>
              <span className="verification-row-value" role="cell">
                {formatSignedCurrency(derived.consumptionCost)}
              </span>
            </div>
            <div className="verification-row" role="row">
              <span className="verification-row-label" role="cell">Transfer</span>
              <span className="verification-row-value" role="cell">
                {formatSignedCurrency(derived.transferCost)}
              </span>
            </div>
            <div className="verification-row" role="row">
              <span className="verification-row-label" role="cell">Storage</span>
              <span className="verification-row-value" role="cell">
                {formatSignedCurrency(derived.storageCost)}
              </span>
            </div>
            <div className="verification-row verification-row--total" role="row">
              <span className="verification-row-label" role="cell">Total</span>
              <span className="verification-row-value" role="cell">
                {formatSignedCurrency(derived.totalCost)}
              </span>
            </div>
          </div>

          {costAnomalies.length > 0 || storageRoundTrip ? (
            <ul
              className="verification-anomaly-list"
              data-testid="cost-anomalies"
            >
              {costAnomalies.map((msg) => (
                <li key={msg}>{msg}</li>
              ))}
              {storageRoundTrip ? (
                <li>{storageRoundTrip.message}</li>
              ) : null}
            </ul>
          ) : null}

          <details className="verification-source-attribution">
            <summary>Source attribution</summary>
            <dl>
              {SOURCE_ATTRIBUTION_COST.map((row) => (
                <React.Fragment key={row.label}>
                  <dt>{row.label}</dt>
                  <dd>{row.value}</dd>
                </React.Fragment>
              ))}
            </dl>
          </details>
        </section>
      </div>

      {/* Timeseries raw table --------------------------------------- */}
      <TimeseriesRawTable />

      {/* Daily transfer range chart -------------------------------- */}
      <TransferDailyRangeChart />
    </div>
  );
}
